import * as React from 'react';
import { compose, withHandlers, withState } from 'recompose';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { skin, OemProps } from 'ui/styles/skin';
import { State } from 'reducers';
import { Button } from './Button';
import { SpeedSlider } from './SpeedSlider';
import { CellIndex, DeviceId } from 'types';
import { ptzMove, ptzSetLocation, ptzLock } from 'actions/cameraActions';
import { getCamera } from 'selectors/deviceSelectors';
import { getLayout } from 'selectors/monitoringSelectors';

const images = {
    home: require('assets/images/ptzh.png'),
    up: require('assets/images/ptzu.png'),
    left: require('assets/images/ptzl.png'),
    down: require('assets/images/ptzd.png'),
    right: require('assets/images/ptzr.png'),
    lock: require('assets/images/ptzlk.png'),
    unlock: require('assets/images/ptzul.png'),
    zoomIn: require('assets/images/zoom+.png'),
    zoomOut: require('assets/images/zoom-.png'),
    preset1: require('assets/images/p1.png'),
    preset2: require('assets/images/p2.png'),
    preset3: require('assets/images/p3.png'),
    preset4: require('assets/images/p4.png'),
    preset5: require('assets/images/p5.png'),
    preset6: require('assets/images/p6.png'),
    preset7: require('assets/images/p7.png'),
    preset8: require('assets/images/p8.png'),
    preset9: require('assets/images/p9.png'),
};

const presets = [ 1, 2, 3, 4, 5, 6, 7, 8, 9 ];

type Props = OwnProps & StateProps & DispProps & RecomposeProps & TranslateProps & OemProps;
export const RawPtzControls = ({ cellIndex, showZoomButtons, showPtzPresets, showSpeedSlider, t, styles, ...props }: Props) =>
    <div className="ptz-controls">
        <div className="button-row">
            <Button
                id={ `ptz-lock-${cellIndex}` }
                titleKey="6.1241.4"
                imgSrc={ images.lock }
                onClick={ props.onLock }
            />
            <Button
                id={ `ptz-up-${cellIndex}` }
                titleKey="1553"
                imgSrc={ images.up }
                onMouseDown={ props.onMoveUp }
                onMouseUp={ props.onStopMoving }
            />
            <Button
                id={ `ptz-unlock-${cellIndex}` }
                titleKey="6.1241.3"
                imgSrc={ images.unlock }
                onClick={ props.onUnlock }
            />
        </div>
        <div className="button-row">
            <Button
                id={ `ptz-left-${cellIndex}` }
                titleKey="1552"
                imgSrc={ images.left }
                onMouseDown={ props.onMoveLeft }
                onMouseUp={ props.onStopMoving }
            />
            <Button
                id={ `ptz-home-${cellIndex}` }
                titleKey="1009"
                imgSrc={ images.home }
                onClick={ props.onHome }
            />
            <Button
                id={ `ptz-right-${cellIndex}` }
                titleKey="1555"
                imgSrc={ images.right }
                onMouseDown={ props.onMoveRight }
                onMouseUp={ props.onStopMoving }
            />
        </div>
        <div className="button-row">
            { showZoomButtons &&
                <Button
                    id={ `ptz-zoomin-${ cellIndex }` }
                    titleKey="1558"
                    imgSrc={ images.zoomIn }
                    onMouseDown={ props.onZoomIn }
                    onMouseUp={ props.onStopMoving }
                />
            }
            <Button
                id={ `ptz-down-${ cellIndex }` }
                titleKey="1554"
                imgSrc={ images.down }
                onMouseDown={ props.onMoveDown }
                onMouseUp={ props.onStopMoving }
            />
            { showZoomButtons &&
                <Button
                    id={ `ptz-zoomout-${ cellIndex }` }
                    titleKey="1559"
                    imgSrc={ images.zoomOut }
                    onMouseDown={ props.onZoomOut }
                    onMouseUp={ props.onStopMoving }
                />
            }
        </div>
        { showSpeedSlider &&
            <div className="button-row">
                <SpeedSlider
                    cellIndex={ cellIndex }
                    speed={ props.speed }
                    onChangeSpeed={ props.onChangeSpeed }
                />
            </div>
        }
        { showPtzPresets &&
            <div className="ptz-presets">
                { presets.map(i =>
                    props[`preset${i}Label`]
                        ? <Button
                            key={ i }
                            id={ `ptz-preset${i}-${cellIndex}` }
                            title={ props[`preset${i}Label`] }
                            imgSrc={ images[`preset${i}`] }
                            onClick={ props[`onPreset${i}`] }
                        />
                        : null
                ) }
            </div>
        }
    </div>
;

type OwnProps = {
    cellIndex: CellIndex;
    deviceId: DeviceId;
};
type StateProps = {
    showZoomButtons: boolean;
    showPtzPresets: boolean;
    showSpeedSlider: boolean;
    preset1Label: string;
    preset2Label: string;
    preset3Label: string;
    preset4Label: string;
    preset5Label: string;
    preset6Label: string;
    preset7Label: string;
    preset8Label: string;
    preset9Label: string;
};
type DispProps = {
    onMoveUp: () => void;
    onMoveDown: () => void;
    onMoveLeft: () => void;
    onMoveRight: () => void;
    onStopMoving: () => void;
    onHome: () => void;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onLock: () => void;
    onUnlock: () => void;
    onPreset1: () => void;
    onPreset2: () => void;
    onPreset3: () => void;
    onPreset4: () => void;
    onPreset5: () => void;
    onPreset6: () => void;
    onPreset7: () => void;
    onPreset8: () => void;
    onPreset9: () => void;
};
type RecomposeProps = {
    speed: number;
    setSpeed: (speed: number) => void; // For internal use only
    onChangeSpeed: (speed: number) => void;
};

const mapStateToProps = (state: State, ownProps: OwnProps): StateProps => {
    const camera = getCamera(state, ownProps);
    const size = getLayout(state);
    return {
        showZoomButtons: camera ? camera.canControlZoom : false,
        showPtzPresets: size <= 4 && camera ? camera.canPtzPresets : false,
        showSpeedSlider: size <= 4,
        preset1Label: camera && camera.ptzLocations[0] || '',
        preset2Label: camera && camera.ptzLocations[1] || '',
        preset3Label: camera && camera.ptzLocations[2] || '',
        preset4Label: camera && camera.ptzLocations[3] || '',
        preset5Label: camera && camera.ptzLocations[4] || '',
        preset6Label: camera && camera.ptzLocations[5] || '',
        preset7Label: camera && camera.ptzLocations[6] || '',
        preset8Label: camera && camera.ptzLocations[7] || '',
        preset9Label: camera && camera.ptzLocations[8] || '',
    };
};
const mapDispatchToProps = (dispatch: Function, { deviceId, speed }: OwnProps & RecomposeProps): DispProps => {
    const makeMove = (x: number, y: number, z: number) => () => dispatch(ptzMove({ deviceId, x, y, z, speed }));
    const makePreset = (locationId: number) => () => dispatch(ptzSetLocation({ deviceId, locationId }));

    return {
        onMoveUp: makeMove(0, -1, 0),
        onMoveDown: makeMove(0, 1, 0),
        onMoveLeft: makeMove(-1, 0, 0),
        onMoveRight: makeMove(1, 0, 0),
        onZoomIn: makeMove(0, 0, 1),
        onZoomOut: makeMove(0, 0, -1),
        onStopMoving: makeMove(0, 0, 0),
        onHome: () => dispatch(ptzLock({ deviceId, lock: false })), // Not a copy-paste error
        onLock: () => dispatch(ptzLock({ deviceId, lock: true })),
        onUnlock: () => dispatch(ptzLock({ deviceId, lock: false })),
        onPreset1: makePreset(0),
        onPreset2: makePreset(1),
        onPreset3: makePreset(2),
        onPreset4: makePreset(3),
        onPreset5: makePreset(4),
        onPreset6: makePreset(5),
        onPreset7: makePreset(6),
        onPreset8: makePreset(7),
        onPreset9: makePreset(8),
    };
};

export const PtzControls = compose<Props, OwnProps>(
    skin,
    translate(),
    withState('speed', 'setSpeed', 3),
    withHandlers<OwnProps & RecomposeProps, Partial<RecomposeProps>>({
        onChangeSpeed: ({ setSpeed }) => (value: number) => {
            setSpeed(Math.max(1, Math.min(Math.round(value), 5))); // Integer between 1 and 5
        },
    }),
    connect<StateProps, DispProps, {}>(mapStateToProps, mapDispatchToProps),
)(RawPtzControls);



// WEBPACK FOOTER //
// ./src/ui/pages/monitoring/mainPanel/controls/PtzControls.tsx